import { Key, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { MdOutlineDelete, MdOutlineEdit, MdOutlineMoreHoriz } from 'react-icons/md';
import { TmdxAvatar } from '@ds';
import { ActionMenu, MenuItemType, TableColumnType } from '@ui';
import { makeCellTextRenderers } from '@features/components/TableCellTextMR';
import { TXCenter, TXCenterListFilters } from '@/api/apis/facilities';
import { getShortTzByIanaName } from '@/utils/helpers';
import { col, COMMON_COLUMN_PRESETS } from '../../utils';

export const TX_CENTERS_LIST_FILTERS_BASE: TXCenterListFilters = {
  countries: [],
  states: [],
  trialProgramIds: [],
};

export const FILTERS_NAMES: Record<string, string> = {
  countries: 'Country',
  states: 'State',
  trialProgramIds: 'Trial Programs',
};

export const TX_CENTER_TABLE_COLUMNS: TableColumnType[] = [
  col('name', 'TX CENTER Name', {
    sortable: true,
    minWidthClassName: 'min-w-80',
  }),
  col('trialProgramName', 'Trial Program', {
    sortable: true,
    minWidthClassName: 'min-w-48',
  }),
  COMMON_COLUMN_PRESETS.code,
  col('qadCustomerId', 'QAD Customer ID', {
    sortable: false,
    minWidthClassName: 'min-w-[87px]',
  }),
  COMMON_COLUMN_PRESETS.timezone,
  {
    ...COMMON_COLUMN_PRESETS.address,
    minWidthClassName: 'min-w-60',
  },
  COMMON_COLUMN_PRESETS.city,
  COMMON_COLUMN_PRESETS.state,
  COMMON_COLUMN_PRESETS.postalCode,
  COMMON_COLUMN_PRESETS.actions,
];

const getActionsMenu = (onEdit: () => void, onDelete: () => void): MenuItemType[] => [
  {
    key: 'edit-tx-center',
    type: 'action',
    to: 'edit-tx-center',
    label: (
      <div className="flex items-center gap-2">
        <MdOutlineEdit size={16} />
        <span className="text-m-r">Edit</span>
      </div>
    ),
    onAction: () => onEdit(),
  },
  {
    key: 'delete-tx-center',
    type: 'action',
    to: 'delete-tx-center',
    label: (
      <div className="flex items-center gap-2 text-danger">
        <MdOutlineDelete size={16} />
        <span className="text-m-r">Delete</span>
      </div>
    ),
    onAction: () => onDelete(),
  },
];

export const getRenderTXCenterTableCell =
  (
    onEdit: (txCenterId: string) => void,
    onDelete: (txCenterId: string) => void,
    resolveSubdivisionName: (stateCode?: string | null, countryCode?: string | null) => string | undefined,
  ) =>
  (txCenter: TXCenter, columnKey: Key): ReactNode => {
    const { id, logoPath, name, code, location, qadCustomerId, trialPrograms } = txCenter;

    const trialProgramsList = trialPrograms?.map((i) => i.name).join('; ') || '-';

    const onTXCenterEdit = () => onEdit(id);
    const onTXCenterDelete = () => onDelete(id);

    const ACTIONS_MENU = getActionsMenu(onTXCenterEdit, onTXCenterDelete);

    const { text, textU } = makeCellTextRenderers();

    switch (columnKey) {
      case 'name':
        return (
          <Link to={`/admin/customers/tx_centers/${id}`} className="flex items-center gap-2">
            <TmdxAvatar radius="xl" size="xl" type="facility" src={logoPath} />
            <span className="text-m-m">{name}</span>
          </Link>
        );
      case 'trialProgramName':
        return textU(trialProgramsList);
      case 'code':
        return textU(code || '-');
      case 'qadCustomerId':
        return textU(qadCustomerId ?? '-');
      case 'timezone':
        return textU(getShortTzByIanaName(location?.timezone) || '-');
      case 'address':
        return text(location?.addressLine || '-');
      case 'city':
        return text(location?.city || '-');
      case 'state':
        return textU(resolveSubdivisionName(location?.state, location?.country) || '-');
      case 'postalCode':
        return text(location?.postalCode || '-');
      case 'actions':
        return (
          <ActionMenu
            placement="left-start"
            items={ACTIONS_MENU}
            MenuTrigger={
              <div className="">
                <MdOutlineMoreHoriz size={20} />
              </div>
            }
            label={`${name} actions menu`}
          />
        );

      default:
        return null;
    }
  };
